import type { ApolloCache, Reference, StoreObject } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/gql'
import type { CommentData, DismissCommentMutation } from '~publish/gql/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { FilterStatus } from '../CommentsFilters/types'
import { useCommentStatus } from '../CommentsFilters/useFilters'

export const DismissCommentMutationGQL = graphql(/* GraphQL */ `
  mutation DismissComment($input: DismissCommentInput!) {
    dismissComment(input: $input) {
      __typename
      ... on DismissCommentSuccess {
        comment {
          ... on BaseComment {
            __typename
            id
            status
            text
            updatedAt
            parentId
          }
          ... on CommentFromThirdParty {
            labels
          }
          ... on CommentFromBuffer {
            publishingStatus {
              status
              errorMessage
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const updateCacheAfterDismiss = (
  cache: ApolloCache<unknown>,
  data: DismissCommentMutation | null | undefined,
  status: FilterStatus,
): void => {
  const result = data?.dismissComment
  if (!isOfType(result, 'DismissCommentSuccess')) {
    return
  }

  const updatedComment = result.comment
  if (!updatedComment) return

  // 1. Update the comment in the cache with the new status
  cache.modify({
    id: cache.identify({
      __typename: updatedComment.__typename,
      id: updatedComment.id,
    }),
    fields: {
      status: () => updatedComment.status,
    },
  })

  // 2. Decide if the comment should be removed from the current view
  // For dismiss, we remove from the list if we're in the "unreplied" view
  const shouldRemoveFromList = status === 'unreplied'

  if (shouldRemoveFromList) {
    // Remove the comment from all comments queries in the cache
    cache.modify({
      fields: {
        comments: (
          existingCommentsRef: Reference | StoreObject,
          { readField },
        ) => {
          const edges =
            readField<{ node: CommentData }[]>('edges', existingCommentsRef) ||
            []

          // Filter out the comment from the edges array
          const newEdges = edges.filter((edge: { node: CommentData }) => {
            const commentId = readField('id', readField('node', edge))
            return commentId !== updatedComment.id
          })

          // Return the new list without the dismissed comment
          return {
            ...existingCommentsRef,
            edges: newEdges,
          }
        },
      },
    })
  }
}

type UseDismissCommentResult = {
  dismissComment: (commentId: string) => Promise<void>
  loading: boolean
}

export const useDismissComment = (): UseDismissCommentResult => {
  const status = useCommentStatus()

  const [mutate, { loading }] = useTypedMutation(
    DismissCommentMutationGQL,
    (data) => data.dismissComment,
    {
      successTypename: 'DismissCommentSuccess',
      update: (cache, { data }) => {
        updateCacheAfterDismiss(cache, data, status)
      },
    },
  )

  const dismissComment = async (commentId: string): Promise<void> => {
    try {
      const result = await mutate({
        variables: {
          input: {
            commentId,
          },
        },
      })

      if (result.success) {
        toast.success('Comment resolved successfully', {
          duration: 1_000,
        })
      } else {
        // eslint-disable-next-line no-console
        console.error('Error resolving comment:', result.error)
        toast.error('An error occurred while resolving the comment')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error resolving comment:', error)
      toast.error('An error occurred while resolving the comment')
    }
  }

  return {
    dismissComment,
    loading,
  }
}
